/**
 * Configuration for inline citation badges in AI chat responses.
 *
 * Controls which sources are excluded from citation numbering,
 * display behavior, and scroll-to-source settings.
 */

export const CITATIONS_CONFIG = {
  /** Feature flag name used to gate inline citations */
  FEATURE_FLAG: 'CHAT_INLINE_CITATIONS',

  /**
   * Sources matching these patterns will be excluded from citation numbering.
   * Matched against the source's `url`, `pageUrl`, `docUrl`, and `pageSource` fields.
   */
  EXCLUDED_LINK_PATTERNS: [] as string[],

  /**
   * Source titles matching these patterns (case-insensitive) will be excluded.
   */
  EXCLUDED_TITLE_PATTERNS: [] as string[],

  /**
   * Source types to exclude from citation numbering entirely.
   * Valid values: 'INFO', 'LAW', 'MODIFIED_LAW', 'CASELAW', 'FORM'
   */
  EXCLUDED_SOURCE_TYPES: [] as string[],

  /** Maximum number of sources to assign citation numbers to */
  MAX_CITATIONS: 50,

  /** UI settings for the citation badges */
  BADGE: {
    /** Base CSS classes for the citation badge */
    CLASS: 'citation-badge',
    /** Whether to show a tooltip preview on hover */
    SHOW_TOOLTIP: true,
    /** Tooltip display delay in ms */
    TOOLTIP_DELAY_MS: 300,
  },

  /** Scroll-to-source behavior settings */
  SCROLL: {
    /** Delay (ms) after tab switch before scrolling (for DOM to settle) */
    TAB_SWITCH_DELAY_MS: 300,
    /** Scroll behavior */
    BEHAVIOR: 'smooth' as ScrollBehavior,
    /** Scroll block alignment */
    BLOCK: 'center' as ScrollLogicalPosition,
    /** Duration (ms) of the highlight animation on the target source */
    HIGHLIGHT_DURATION_MS: 2000,
  },

  /** Regex pattern to match citation markers in LLM output: [1], [2], etc. */
  CITATION_REGEX: /\[(\d+)\]/g,

  /**
   * ID prefix for source DOM elements, used for scroll targeting.
   * Full ID format: `${PREFIX}${adviceId}-${citationIndex}`
   */
  SOURCE_DOM_ID_PREFIX: 'citation-source-',
} as const;
